import { motion, useReducedMotion } from 'motion/react';
import { efficiencyLandingCopy } from '../../lib/strings/efficiency-landing';
import { Button } from '../../ui';
import { openSupport } from '../../lib/dock';
import { paidSpan } from '../../lib/entitlement';
import { approxMoney, utcDate } from '../../lib/format';
import { easeSlow, prefersReducedMotion } from '../../lib/motion';
import { sound } from '../../lib/sound';
import { track } from '../../lib/events';
import { useLocale, useStrings } from '../../state/useLocale';
import type { AccountApi } from '../../state/useAccount';
import type { Offer as OfferData } from '../../lib/offer';
import type { Plan } from '../../lib/checkout';
import type { PublicMission } from '../../types';
import type { CertificateProgress } from '../../lib/certificates';

/**
 * The offer: what the rest of the course is, what it costs, and the two ways
 * to be given it.
 *
 * ## Why this exists at all
 *
 * For a while the product had a paywall and no price. The paid half was built,
 * the checkout was built, the entitlement table was built — and the only way
 * a learner could find out what any of it cost was to press a button that
 * navigated them to a payment provider, where the number appeared for the
 * first time. That is not a pricing mistake, it is a missing screen: a course
 * with thirty levels behind a wall has to be able to say what is behind the
 * wall and what it costs, in the place where somebody is deciding.
 *
 * ## Where it sits, and why it is not a wall
 *
 * At the foot of the ledger, after the levels. The learner reaches it having
 * just scrolled past everything they cleared for nothing, which is the only
 * honest place to make the argument: the free half is not a trailer, it is
 * twenty levels, and the offer reads as "here is the rest" rather than "you
 * have run out". Nothing above it is obscured, dimmed or teased.
 *
 * ## And on the landing page, which is the newer half of that
 *
 * The same block, under the chapters, on `/`. For a long time it was on the
 * ledger only — and the ledger stopped being the front page on 2026-09-17
 * (lib/landing.ts), which left the product in the state it was built to get
 * out of: a stranger could read the whole pitch, decide they wanted it, and
 * find no price and no way to pay anywhere on the page they were on. The
 * price was one click away, behind a link most readers had no reason to
 * press, on a screen that boots a virtual machine first.
 *
 * So `place` exists, and it is not decoration. `offer-seen` is the
 * denominator of every rate below it (docs/analytics.md), and a block that
 * silently began firing it from a second screen would move the offer-to-
 * checkout rate with nobody having edited a number. The word says which page
 * the impression happened on; the Worker's allowlist is what keeps it to two
 * (worker/events.ts).
 *
 * It is also not gated on progress. The button used to appear only once every
 * free level was cleared, which meant that for the entire time a learner was
 * actually using the product there was no way to buy it — the one state in
 * which somebody is most likely to want the rest of a course is halfway
 * through enjoying it, not at the end of a twenty-level grind. Now the block
 * is there from the first visit for anybody who does not already own it, and
 * the *hero* button still follows progress, because that one is about what to
 * play next rather than about what to buy.
 *
 * ## The three states it has to draw
 *
 * - **Payable.** The price and a button that starts a checkout.
 * - **Priced but not payable.** The price, and a plain sentence saying card
 *   payments are not on here yet. This is the state the product actually
 *   shipped in — a payment account existed and the keys that make it usable
 *   did not — and it is a state any deployment can be in, since the Worker
 *   turns the checkout off unless all four Stripe values are set
 *   (worker/stripe.ts). It is the reason `payable` is a separate fact from
 *   `price` in the Worker's answer. Hiding the number in this state would be
 *   the old bug with extra steps.
 * - **Nothing known.** No offer, or no price in it: the block does not draw.
 *   A price is the one thing here that must never be guessed at, and an empty
 *   space is a better answer than a confident wrong one.
 *
 * The invite-code link is offered in all of them, whenever the deployment has
 * codes configured, and is the *only* route through in the second state.
 *
 * ## The reader with no card
 *
 * Under all three states there is one more line, and it is there because the
 * three states above are all about *us*: whether the deployment can take
 * money. None of them is about the commonest reason a willing reader does not
 * buy, which is that Stripe takes a card, Apple Pay or Google Pay and they
 * hold none of the three — no card at all, or one their bank will not let out
 * of the country. That reader was being shown a price and a button they could
 * not press, which is a wall with a number on it.
 *
 * So the block says so, and opens the support desk (lib/dock.ts). It promises
 * a person, not a payment method: naming a wallet nobody has set up yet would
 * be the one kind of sentence this product cannot afford.
 *
 * The desk and not an address. It was a `mailto:` for about an hour, and the
 * address was real in the sense that it is printed in the terms — and the
 * domain has no MX record, so every one of those messages would have bounced
 * at exactly the reader least able to find another way in. The desk is a room
 * with history, attached to an account somebody can look up, and it is the
 * thing that is actually staffed (docs/support.md). On the ledger it is in
 * the corner of the screen already; on the landing page pressing this is what
 * mounts it.
 */
export function Offer({
  missions,
  account,
  offer,
  place,
  certificate = null,
  onUnlock,
  onInvite,
}: {
  missions: PublicMission[];
  account: AccountApi;
  offer: OfferData | null;
  /** Which page this impression happened on. See the note above. */
  place: 'landing' | 'ledger';
  /**
   * The certificate this free account is closest to, when it has passed a
   * checkpoint toward one (lib/certificates.ts, `closestCertificate`). The
   * block then says how far they are and that certificates come with the
   * course — the contract's own offer (docs/certificates.md, "Who gets
   * one"): somebody four checkpoints into six is two away, and the course is
   * what stands between. Said once, as a fact, beside the price it is about.
   */
  certificate?: CertificateProgress | null;
  onUnlock(plan?: Plan): void;
  onInvite(): void;
}) {
  const strings = useStrings();
  const s = strings.home.offer;
  const { locale } = useLocale();
  const efficiency = efficiencyLandingCopy[locale];
  // Both, for the reason Home gives where it does the same: the hook tracks
  // the media query live and the function covers the render before it has
  // resolved.
  const reduced = useReducedMotion() ?? prefersReducedMotion();
  const span = paidSpan(missions);
  // The heading's count, on the same rule: levels, not checkpoints, so it
  // agrees with the "N levels" the landing page prints above it.
  const paidLevels = missions.filter((m) => m.locked && m.track !== 'checkpoint').length;

  // Nothing locked means nothing to sell: this account has the course, or this
  // build has no paid half. Either way the block is not on the page.
  if (!span) return null;
  // No price, no offer. See "Nothing known" above.
  if (!offer || offer.price === null || offer.currency === null) return null;

  // In the reader's own currency when the Worker could tell which it is
  // (lib/offer.ts, `local`), and in the charged currency otherwise.
  const { local } = offer;
  const shown = (amount: number) =>
    local ? approxMoney(amount, offer.currency!, local, locale) : s.price(amount, offer.currency!);

  function handleBuy() {
    sound.click();
    if (!account.user) { account.signIn(); return; }
    onUnlock();
  }

  function handleMonth() {
    if (!account.user) { account.signIn(); return; }
    sound.click();
    onUnlock('month');
  }

  // How much less a month costs inside the three months, from the two
  // figures actually charged, so the sentence cannot disagree with them. Not
  // said when it is not a saving: a deployment that priced the month at a
  // third of the quarter or less has nothing to compare.
  const { month } = offer;
  const monthSaving = month ? Math.round((1 - offer.price / 3 / month.price) * 100) : 0;

  function handleCode() {
    sound.click();
    onInvite();
  }

  /* No prop for this one, and that is the same decision `Profile` made for a
     team's room: the dock is not this component's parent, its child or its
     sibling on every screen it is drawn on, so the ask goes through the store
     that exists for exactly that (lib/dock.ts). */
  function handleSupport() {
    sound.click();
    openSupport();
  }

  return (
    <motion.section
      className="offer"
      /* The band's own address, so a bar link can point at the price. It is
         on the section rather than on a wrapper around it, and that is not a
         detail: `.home > *` is what gives every band its gutter and its
         full-bleed hairline (styles/home.css), so an extra div here would
         inset the rule above the price by the width of the margin and make
         this the one band on the page whose top edge stops short. */
      id="pricing"
      aria-labelledby="offer-title"
      initial={reduced ? false : { opacity: 0, y: 12 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={easeSlow}
      /* The block reached the screen, which is the denominator the two
         buttons below are worth nothing without: "nobody bought it" and
         "nobody scrolled far enough to see the price" are the same number of
         sales and completely different problems (docs/analytics.md). It hangs
         on the viewport callback the entrance animation already uses, so it
         fires once, on the same `amount: 0.3` threshold, and costs no second
         observer — and it fires for a reduced-motion visitor too, whose
         `whileInView` is undefined but whose `viewport` is still watched. */
      onViewportEnter={() => track('offer-seen', { detail: place })}
    >
      <div className="offer-copy">
        <p className="label">{efficiency.offerLabel}</p>
        <h2 className="offer-title" id="offer-title">
          {/* The heading names the rows when the catalogue can name them and
              says how many there are when it cannot. A range is offered only
              while the paid half is one lane's tail (lib/entitlement.ts), and
              a wholly paid track's `0-19` would be a heading advertising
              twenty levels this learner already played. The lede underneath
              has always been a count and does not move. */}
          {efficiency.offerTitle}
        </h2>
        <p className="offer-lede">{efficiency.offerBody(paidLevels)}</p>
        {certificate && (
          <p className="offer-cert">
            {strings.certificateCourse.offer(
              certificate.passed.length,
              certificate.missions.length,
              strings.certificate.names[certificate.certificate],
            )}
          </p>
        )}
        <ul className="offer-points">
          {efficiency.offerPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </div>

      <div className="offer-act">
        {/* The price is the largest thing in this half, and it is always the
            figure the checkout charges. During the launch sale (owner,
            2026-09-28: half price until 1 January 2027) the regular price is
            struck through beside it and the end of the sale is said under it;
            the percentage is worked out from the two numbers rather than
            written down, so it cannot disagree with them.

            In the reader's own currency, the big figure is an approximation
            (owner, 2026-09-28), and the exact charge is said right under it
            in the currency the checkout uses — the one number here that
            is not "about". */}
        <p className="offer-price">
          {offer.regular !== null && (
            <>
              <s className="offer-regular">{shown(offer.regular)}</s>
              <span className="sr-only">{s.regularLabel}</span>{' '}
            </>
          )}
          {shown(offer.price)}
        </p>
        {local && <p className="note offer-charged">{s.charged(s.price(offer.price, offer.currency))}</p>}
        {/* A regional price says whose it is, once, plainly (owner,
            2026-10-03; docs/regional-pricing.md). No flag and no "discount"
            banner: every figure on this block is already that country's, and
            the struck-through one is that country's regular price. */}
        {offer.region && <p className="note offer-region">{s.regional(countryName(offer.region.country, locale))}</p>}
        {offer.regular !== null && offer.saleUntil !== null && (
          <p className="offer-sale">
            {s.sale(
              Math.round((1 - offer.price / offer.regular) * 100),
              utcDate(new Date(offer.saleUntil), locale),
            )}
          </p>
        )}
        <p className="note">{s.priceWhy}</p>

        {offer.payable ? (
          <>
            <Button variant="solid" size="lg" onClick={handleBuy} title={s.buyWhy}>
              {s.buy}
            </Button>
            {/* Said before the button is pressed, not after it bounces them:
                discovering mid-checkout that an account is needed is the worst
                possible place to find out. */}
            {!account.user && <p className="note offer-note">{s.signedOut}</p>}
          </>
        ) : (
          <p className="offer-off" role="status">
            {s.off}
          </p>
        )}

        {/* One month (owner, 2026-09-29): under the three months and smaller,
            because it is the way in and not the offer. Its price is drawn by
            the same rules as the big one — struck through during the sale,
            approximate in the reader's currency with the exact charge under
            it — and its button only when the checkout is on. */}
        {month && (
          <div className="offer-month">
            <p className="offer-month-price">
              <span className="offer-month-label">{s.monthLabel}</span>{' '}
              {month.regular !== null && (
                <>
                  <s className="offer-regular">{shown(month.regular)}</s>
                  <span className="sr-only">{s.regularLabel}</span>{' '}
                </>
              )}
              <span className="offer-month-amount">{shown(month.price)}</span>
            </p>
            {local && <p className="note offer-charged">{s.charged(s.price(month.price, offer.currency))}</p>}
            {monthSaving > 0 && <p className="note">{s.monthWhy(monthSaving)}</p>}
            {offer.payable && (
              <Button variant="quiet" onClick={handleMonth} title={s.buyWhy}>
                {s.monthBuy}
              </Button>
            )}
          </div>
        )}

        {/* Offered in every state, and the only way through when the checkout
            is off. A quiet button rather than a link: redeeming is an action
            with a side effect and does not belong in the address bar. */}
        {offer.invite && (
          <Button variant="quiet" onClick={handleCode}>
            {s.haveCode}
          </Button>
        )}

        {/* Last, and quiet, and unconditional. Somebody who can press the
            button above has already stopped reading; somebody who cannot is
            at the end of the block with nothing else left, which is exactly
            where the way out belongs.

            A button and not a link, because nothing here is a place: the desk
            is a panel over whatever screen you are on, and putting it in the
            address bar would make asking a question a navigation with a back
            button in the middle of it (lib/dock.ts says this at length). It is
            drawn as a word in a sentence all the same — what follows the
            question mark is the answer to it, not a second control competing
            with the price. */}
        <p className="note offer-help">
          {s.noCard}{' '}
          <button type="button" className="offer-help-ask" onClick={handleSupport}>
            {s.noCardLink}
          </button>
        </p>
      </div>
    </motion.section>
  );
}

/**
 * A country's name in the page's language, from its ISO code, by `Intl`, so
 * no catalogue of country names has to be kept in four languages. A runtime
 * that cannot name it shows the code, which is still true.
 */
function countryName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}
